/**
 * 语言偏好工具函数
 * 
 * 这个文件提供了一组函数，用于管理用户的语言偏好设置
 * 使用localStorage存储语言偏好，并在API请求中包含相应的头信息
 */

// localStorage中存储语言偏好的键名
const LOCALE_STORAGE_KEY = 'plant_care_locale';

/**
 * 获取用户当前语言偏好
 * 
 * @returns {string} 语言代码 (例如 'zh-CN', 'en-US')
 */
export function getUserLocale() {
  // 从localStorage获取存储的语言偏好
  const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
  
  if (storedLocale) {
    return storedLocale;
  }
  
  // 如果没有存储的语言偏好，尝试使用浏览器语言
  const browserLocale = navigator.language || navigator.userLanguage || 'en-US';
  
  // 存储浏览器语言作为默认值
  setUserLocale(browserLocale);
  
  return browserLocale;
}

/**
 * 设置用户语言偏好
 * 
 * @param {string} locale - 语言代码 (例如 'zh-CN', 'en-US')
 */
export function setUserLocale(locale) {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  
  // 触发自定义事件，通知应用程序语言已更改
  window.dispatchEvent(new CustomEvent('localeChanged', { detail: { locale } }));
}

/**
 * 检测语言是否为中文
 * 
 * @param {string} locale - 语言代码
 * @returns {boolean} 是否为中文
 */
export function isChinese(locale) {
  return locale && (locale.startsWith('zh') || locale.includes('zh-'));
}

/**
 * 为API请求添加语言偏好头信息
 * 
 * @param {Object} headers - 请求头对象
 * @returns {Object} 添加了语言偏好的请求头对象
 */
export function addLocaleToHeaders(headers = {}) {
  const locale = getUserLocale();
  
  return {
    ...headers,
    'X-Preferred-Language': locale,
    'Accept-Language': locale
  };
}

/**
 * 获取基于当前语言的文本
 * 
 * @param {Object} textMap - 文本映射对象，键为语言代码，值为相应文本
 * @param {string} defaultText - 默认文本（如果找不到匹配的语言）
 * @returns {string} 当前语言的文本
 */
export function getLocalizedText(textMap, defaultText = '') {
  const locale = getUserLocale();
  
  // 尝试精确匹配
  if (textMap[locale]) {
    return textMap[locale];
  }
  
  // 尝试语言代码前缀匹配
  const prefix = locale.split('-')[0];
  
  // 查找所有以该前缀开头的键
  const matches = Object.keys(textMap).filter(key => key.startsWith(prefix));
  
  if (matches.length > 0) {
    return textMap[matches[0]];
  }
  
  // 如果有中文设置且用户语言是中文
  if (isChinese(locale) && textMap['zh']) {
    return textMap['zh'];
  }
  
  // 如果有英文设置且没有找到匹配
  if (textMap['en']) {
    return textMap['en'];
  }
  
  // 返回默认文本
  return defaultText;
} 